import type { UseQueryResult } from "@tanstack/react-query";
import type { ChallengeIdeaFiltersState } from "stores/challengeIdeaFilters";

import type { OpenAPI } from "simplydo/interfaces";
import type {
  ChallengeHeader,
  ChallengeWithCoverImage,
  ChallengeWithIdeaTemplate,
  ChallengeWithOwnerOrganisation,
} from "simplydo/interfaces/types/challengeTypes";
import type { UserMe } from "simplydo/interfaces/types/userTypes";

export type { UserMe };
export type {
  ChallengeHeader,
  ChallengeWithCoverImage,
  ChallengeWithIdeaTemplate,
  ChallengeWithOwnerOrganisation,
};

/** Full challenge payload from GET /challenges/{id}. */
export type ChallengeDetail = OpenAPI.GET<"/challenges/{id}">["response"];

/** The challenge's template; the API sends null without one and `{}` once it is deleted. */
export type ChallengeIdeaTemplate = Exclude<
  ChallengeDetail["ownerIdeaTemplate"],
  null | Record<string, never>
>;

export const isChallengeIdeaTemplate = (
  template: ChallengeDetail["ownerIdeaTemplate"] | undefined,
): template is ChallengeIdeaTemplate => !!template && "_id" in template;

export type ChallengeListItem =
  OpenAPI.GET<"/challenges">["response"]["challenges"][number];

type ClearableChallengeDateFields =
  | "deadline"
  | "openDate"
  | "questionDeadline";

export type ChallengeSettingsUpdate =
  OpenAPI.PUT<"/challenges/{id}">["requestBody"] & {
    aiAssessmentHelper?: boolean;
    aiIdeaFeedback?: boolean;
    aiIdeaAnonymityCheck?: boolean;
    aiIdeaSummarisation?: boolean;
    videoUrl?: string | null;
    bannerImageUrl?: string | null;
    coverImageUrl?: string | null;
    bannerImageThumbnailUrls?: Record<string, string>;
    coverImageUrls?: Record<string, string>;
    defaultIdeaOrder?: ChallengeDetail["defaultIdeaOrder"];
  };

/** Optimistic local updates for challenge settings forms. */
export type ChallengeSettingsLocalUpdate = Omit<
  Partial<ChallengeDetail>,
  | ClearableChallengeDateFields
  | "video"
  | "bannerImage"
  | "coverImage"
  | "projectBoardAutomaticAssignees"
> &
  Pick<
    ChallengeSettingsUpdate,
    | ClearableChallengeDateFields
    | "video"
    | "bannerImage"
    | "coverImage"
    | "videoUrl"
    | "bannerImageUrl"
    | "coverImageUrl"
    | "bannerImageThumbnailUrls"
    | "coverImageUrls"
  >;

export type ChallengeOverview =
  OpenAPI.GET<"/challenges/{id}/overview">["response"];

export type ChallengeProblem =
  OpenAPI.GET<"/challenges/{id}/problems">["response"] & {
    ipManagementMissingStatement?: boolean;
  };

export type ChallengeIdeaListItem =
  OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"][number];

export type ChallengeIdeaFilters =
  OpenAPI.GET<"/challenges/{id}/ideas/filters">["response"];

export type ChallengeQuestion =
  OpenAPI.GET<"/challenges/{challenge_id}/questions">["response"]["questions"][number];

export type ChallengeAssessmentsResponse =
  OpenAPI.GET<"/challenges/{id}/assessments">["response"];

export type IdeaTemplateAssessmentRound = NonNullable<
  NonNullable<
    NonNullable<ChallengeDetail["ownerIdeaTemplate"]>["assessments"]
  >["rounds"]
>[number];

export type AssignedAssessmentIdea =
  ChallengeAssessmentsResponse["ideas"][number];

export type AssignedAssessmentRow = NonNullable<
  NonNullable<AssignedAssessmentIdea["assessments"]>[number]
> & { idea: AssignedAssessmentIdea };

export type ChallengeAssessmentDashboard =
  OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"];

export type ChallengeActivityResponse =
  OpenAPI.GET<"/challenges/{id}/activity">["response"];

export type ChallengePlannerEvent = NonNullable<
  NonNullable<
    OpenAPI.GET<"/calendars/{calendar_id}/calendar">["response"]["calendar"]
  >["events"]
>[number];

export type ChallengeImpactStatementsConfig = NonNullable<
  ChallengeDetail["impactStatements"]
>;

export type ChallengeImpactStatementOption = NonNullable<
  NonNullable<ChallengeImpactStatementsConfig["impacts"]>[string]["options"]
>[number];

export type ChallengeIpStatement = NonNullable<
  NonNullable<ChallengeDetail["ipManagement"]>["ipStatements"]
>[number];

export type ChallengeFollowersResponse =
  OpenAPI.GET<"/challenges/{id}/followers">["response"];

export type ChallengeFollower = ChallengeFollowersResponse["users"][number] & {
  challengeFollowing?: { createdAt?: string };
};

export type ChallengeRouteProps = {
  challenge: ChallengeDetail;
  user: UserMe | null;
};

export type ChallengeSettingsProps = { challenge: ChallengeDetail };

export type IdeaVisibilityLimit = NonNullable<
  ChallengeDetail["ideaVisibilityLimits"]
>[number];

export type IdeaFilterType = NonNullable<ChallengeDetail["defaultIdeaOrder"]>;

export type GenAIChallengePreview = OpenAPI.Schemas["Challenge"] & {
  ownerIdeaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
  coverImageUrl?: string;
  bannerImageUrl?: string;
};

export type GenAIChallengeDraft = Partial<GenAIChallengePreview>;

export type ChallengeProjectLane =
  OpenAPI.GET<"/boards/{for_id}/lanes">["response"]["lanes"][number];

export type ChallengeProjectBoardDefaultLane = NonNullable<
  NonNullable<ChallengeDetail["projectBoard"]>["defaultLane"]
> & { _id?: string; name?: string };

/** Return value of `useChallengeQuery`. */
export type UseChallengeQueryReturn = {
  challengeId: string | undefined;
  challenge: ChallengeDetail | undefined;
  challengeQuery: UseQueryResult<ChallengeDetail, Error>;
  user: UserMe | null;
  canManageChallenge: boolean;
  updateChallenge: (
    update: ChallengeSettingsUpdate,
    onSuccess?: (updated: ChallengeDetail) => void,
    onError?: (err: { message: string }) => void,
  ) => void;
  updateChallengeField: <K extends keyof ChallengeSettingsUpdate>(
    key: K,
    value: ChallengeSettingsUpdate[K],
  ) => void;
};

/** Idea list query params derived from challengeIdeaFilters + view type. */
export type ChallengeIdeaQueryParams = {
  ideaViewType: ChallengeIdeaFiltersState["ideaViewType"];
  page: number;
  query: string;
  tags: ChallengeIdeaFiltersState["tags"];
  status: ChallengeIdeaFiltersState["status"];
  sort: ChallengeIdeaFiltersState["sort"];
  sortDirection: ChallengeIdeaFiltersState["sortDirection"];
  labels: ChallengeIdeaFiltersState["labels"];
  authorGroupIds: ChallengeIdeaFiltersState["authorGroupIds"];
  authorOrganisationIds: ChallengeIdeaFiltersState["authorOrganisationIds"];
  fieldChoices: ChallengeIdeaFiltersState["fieldChoices"];
  limit: number;
  hasAdminComment: ChallengeIdeaFiltersState["hasAdminComment"];
  hasCustomDeadline: ChallengeIdeaFiltersState["hasCustomDeadline"];
  assignees: ChallengeIdeaFiltersState["assignees"];
  lanes: ChallengeIdeaFiltersState["lanes"];
  impactIds: ChallengeIdeaFiltersState["impactIds"];
};
